/**
 * Issuing an invoice or a credit note.
 *
 * This form is the autonomy rule made usable: an installation with no ERP has
 * to be able to produce a numbered document, and the number comes from the
 * installation's own series. When an ERP invoices, documents arrive through
 * the synchronisation instead and nobody opens this dialog — which is why the
 * description says so rather than leaving the desk to wonder whether it
 * should be typing here.
 *
 * The totals are typed rather than computed from lines, and that is the same
 * decision the backend makes: the document's total is the document's, and a
 * screen that recomputed it would disagree with the paper the moment a
 * footer discount or a carriage line exists. The one check made here is that
 * net + tax equals the total, before a wrong document gets a real number.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAction } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { Button, Dialog, Input, Select, useToast } from "@hardauto/core-ui";

import type { BillingApi, DocumentKind } from "./api";
import { NAMESPACE } from "./i18n";

export interface IssueDialogProps {
  api: BillingApi;
  open: boolean;
  onClose: () => void;
  /** Prefilled when the dialog is opened from a customer's own page. */
  clientId?: string;
  clientLabel?: string;
  onIssued: () => void;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Whether the three figures agree, compared as numbers.
 *
 * Decimal strings compared as numbers is safe *for a comparison* — it is the
 * arithmetic on money that a float ruins, and nothing here is stored. The
 * strings themselves are what gets sent.
 */
function totalsAgree(subtotal: string, tax: string, total: string): boolean {
  const sum = Number(subtotal || 0) + Number(tax || 0);
  return Math.abs(sum - Number(total || 0)) < 0.005;
}

export function IssueDialog({
  api,
  open,
  onClose,
  clientId,
  clientLabel,
  onIssued,
}: IssueDialogProps) {
  const { t } = useTranslation(NAMESPACE);
  const action = useAction();
  const toast = useToast();

  const [kind, setKind] = useState<DocumentKind>("invoice");
  const [client, setClient] = useState(clientId ?? "");
  const [issuedOn, setIssuedOn] = useState(today);
  const [dueOn, setDueOn] = useState("");
  const [subtotal, setSubtotal] = useState("");
  const [tax, setTax] = useState("");
  const [total, setTotal] = useState("");
  const [orderReference, setOrderReference] = useState("");
  const [site, setSite] = useState("");
  const [error, setError] = useState<string | null>(null);

  // A fresh document each time it opens. A dialog that reopened carrying the
  // last amounts would produce a duplicate invoice with a new number, which
  // is the expensive kind of duplicate.
  useEffect(() => {
    if (!open) return;
    setKind("invoice");
    setClient(clientId ?? "");
    setIssuedOn(today());
    setDueOn("");
    setSubtotal("");
    setTax("");
    setTotal("");
    setOrderReference("");
    setSite("");
    setError(null);
  }, [open, clientId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("issue.title")}
      description={t("issue.description")}
      closeLabel={t("issue.cancel")}
      footer={
        <>
          <Button onClick={onClose}>{t("issue.cancel")}</Button>
          <Button
            variant="primary"
            busy={action.isBusy("issue")}
            busyLabel={t("issue.submit")}
            onClick={() =>
              void action.run("issue", async () => {
                setError(null);
                if (!client.trim() || !total.trim()) return;
                if (!totalsAgree(subtotal, tax, total)) {
                  setError(t("issue.totalMismatch"));
                  return;
                }
                try {
                  const document = await api.issue({
                    kind,
                    client_id: client.trim(),
                    issued_on: issuedOn,
                    subtotal: subtotal.trim() || total.trim(),
                    tax_amount: tax.trim() || "0",
                    total: total.trim(),
                    // A credit note has no due date — the server drops one
                    // anyway, and sending it would be stating something false.
                    due_on: kind === "invoice" && dueOn ? dueOn : null,
                    order_reference: orderReference.trim(),
                    site_code: site.trim(),
                  });
                  toast.show(t("issue.done", { number: document.number }), { tone: "success" });
                  onIssued();
                  onClose();
                } catch {
                  setError(t("issue.failed"));
                }
              })
            }
          >
            {t("issue.submit")}
          </Button>
        </>
      }
    >
      <div className="hardauto-form">
        <Field label={t("issue.kind")} required>
          {(props) => (
            <Select
              {...props}
              value={kind}
              onChange={(event) => setKind(event.target.value as DocumentKind)}
            >
              <option value="invoice">{t("kind.invoice")}</option>
              <option value="credit_note">{t("kind.credit_note")}</option>
            </Select>
          )}
        </Field>

        {clientLabel ? (
          <Field label={t("issue.client")}>
            {(props) => <Input {...props} value={clientLabel} readOnly />}
          </Field>
        ) : (
          <Field label={t("issue.client")} hint={t("issue.clientHint")} required>
            {(props) => (
              <Input
                {...props}
                value={client}
                onChange={(event) => setClient(event.target.value)}
              />
            )}
          </Field>
        )}

        <Field label={t("issue.issuedOn")} required>
          {(props) => (
            <Input
              {...props}
              type="date"
              value={issuedOn}
              onChange={(event) => setIssuedOn(event.target.value)}
            />
          )}
        </Field>

        {kind === "invoice" ? (
          <Field label={t("issue.dueOn")} hint={t("issue.dueOnHint")}>
            {(props) => (
              <Input
                {...props}
                type="date"
                value={dueOn}
                onChange={(event) => setDueOn(event.target.value)}
              />
            )}
          </Field>
        ) : null}

        <Field label={t("issue.subtotal")}>
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              value={subtotal}
              onChange={(event) => setSubtotal(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("issue.tax")}>
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              value={tax}
              onChange={(event) => setTax(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("issue.total")} required error={error ?? undefined}>
          {(props) => (
            <Input
              {...props}
              inputMode="decimal"
              value={total}
              onChange={(event) => setTotal(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("issue.orderReference")}>
          {(props) => (
            <Input
              {...props}
              value={orderReference}
              onChange={(event) => setOrderReference(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("issue.site")}>
          {(props) => (
            <Input {...props} value={site} onChange={(event) => setSite(event.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
